import {
  Command,
  CommandItem,
  CommandList,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@/components/ui";
import { getPartsFromGeocoderResult } from "@/lib/utils/googleMapUtils";
import { CommandEmpty } from "cmdk";
import React, { useEffect, useState } from "react";
import { type UseFormRegisterReturn } from "react-hook-form";
import { cn } from "@/lib/utils/cn";
import usePlacesAutocomplete, { getGeocode } from "use-places-autocomplete";

interface PlacesAutocompleteFormFieldProps {
  register: UseFormRegisterReturn;
  label?: string;
  labelClassName?: string;
  formItemClassName?: string;
  onAddressSelect: (
    address: ReturnType<typeof getPartsFromGeocoderResult>,
  ) => void;
  defaultValue?: string;
  dataTestId?: string;
  disabled?: boolean;
}

export const PlacesAutocompleteFormField = React.forwardRef<
  HTMLInputElement,
  PlacesAutocompleteFormFieldProps
>(
  (
    {
      register,
      label,
      labelClassName,
      formItemClassName,
      onAddressSelect,
      defaultValue,
      dataTestId,
      disabled,
    },
    ref,
  ) => {
    const {
      ready,
      suggestions: { status, data },
      value,
      setValue,
      clearSuggestions,
    } = usePlacesAutocomplete({
      callbackName: "initMap",
      requestOptions: {
        types: ["address"],
        componentRestrictions: { country: "us" },
      },
      debounce: 300,
    });
    const [isOpen, setIsOpen] = useState(false);
    const handleInput = (
      e:
        | React.ChangeEvent<HTMLInputElement>
        | React.FocusEvent<HTMLInputElement>,
    ) => {
      // Update the keyword of the input element
      setValue(e.target.value);
    };

    useEffect(() => {
      if (defaultValue && !value) {
        setValue(defaultValue);
      }
    }, [defaultValue, value, setValue]);

    const handleSelect =
      ({ description }: google.maps.places.AutocompletePrediction) =>
      async () => {
        // When the user selects a place, we can replace the keyword without request data from API
        // by setting the second parameter to "false"
        clearSuggestions();
        await getGeocode({ address: description }).then((results) => {
          const geocoderParts = getPartsFromGeocoderResult(results[0]);
          onAddressSelect(geocoderParts);
          // setValue(geocoderParts.streetAddress);
          clearSuggestions();
        });
      };

    const renderSuggestions = () =>
      data.map((suggestion) => {
        const {
          place_id,
          structured_formatting: { main_text, secondary_text },
        } = suggestion;

        return (
          <CommandItem
            onMouseDown={(e) => {
              e.preventDefault();
            }}
            key={place_id}
            onSelect={handleSelect(suggestion)}
          >
            <strong>{main_text}</strong> <small>{secondary_text}</small>
          </CommandItem>
        );
      });

    return (
      <>
        <FormField
          {...register}
          // @ts-expect-error This line is to remove a console error
          ref={null}
          render={({ field }) => (
            <FormItem className={cn(formItemClassName)}>
              <div className="flex">
                <FormLabel className={labelClassName}>
                  {label ?? "Street address"}
                </FormLabel>
                <FormMessage
                  data-testid="error-message"
                  className="grow text-right"
                />
              </div>
              <FormControl>
                <Input
                  {...field}
                  type="text"
                  value={field.value}
                  placeholder="Start typing..."
                  id="street-address"
                  data-testid={dataTestId}
                  onChange={(e) => {
                    handleInput(e);
                    field.onChange(e);
                  }}
                  onBlur={() => {
                    setIsOpen(false);
                  }}
                  disabled={disabled || !ready}
                  onFocus={() => {
                    setIsOpen(true);
                  }}
                  innerRef={ref}
                />
              </FormControl>
              <Command className="z-50 w-auto" shouldFilter={false}>
                {isOpen && value && status === "OK" ? (
                  <CommandList>
                    {data.length ? (
                      renderSuggestions()
                    ) : (
                      <CommandEmpty>No results found</CommandEmpty>
                    )}
                  </CommandList>
                ) : null}
              </Command>
            </FormItem>
          )}
        />
      </>
    );
  },
);

PlacesAutocompleteFormField.displayName = "PlacesAutocomplete";
